/**
 * Photo (link-only) + Link. We NEVER upload or store images — the member pastes a public https
 * image link, which is validated before it is accepted and shown in the preview/embed. The
 * submitting Discord user id is recorded on the review for accountability (not the image).
 * @see shared/src/url.ts (isHttpsImageUrl)
 */
import { useState } from 'react';
import { isHttpsImageUrl } from '@pghbites/shared';

interface PhotoLinkProps {
  photoUrl: string;
  link: string;
  onPhotoUrl: (value: string) => void;
  onLink: (value: string) => void;
}

export function PhotoLink({ photoUrl, link, onPhotoUrl, onLink }: PhotoLinkProps) {
  const [draft, setDraft] = useState(photoUrl);
  const invalid = draft.trim() !== '' && !isHttpsImageUrl(draft);

  // Only valid https image links are propagated to the form (so the preview/embed stays clean).
  const handlePhoto = (v: string) => {
    setDraft(v);
    onPhotoUrl(isHttpsImageUrl(v) ? v.trim() : '');
  };

  return (
    <section className="photo-link">
      <p className="photo-note">
        We don’t store photos — paste a public image link (Instagram, Shutterfly, etc.).
      </p>

      <label className="field" htmlFor="photo-url">
        <span className="field-label">📷 Photo link (optional)</span>
        <input
          id="photo-url"
          className="field-input"
          type="url"
          inputMode="url"
          value={draft}
          placeholder="https://… .jpg"
          aria-invalid={invalid || undefined}
          onChange={(e) => handlePhoto(e.target.value)}
        />
        {invalid && (
          <span className="add-own-error" role="alert">
            Use a public https image link ending in .jpg, .png, .gif, .webp, or .avif.
          </span>
        )}
      </label>

      {photoUrl && (
        <div className="photo-preview">
          <img src={photoUrl} alt="Review photo preview" />
        </div>
      )}

      <label className="field" htmlFor="link">
        <span className="field-label">🔗 Link (optional)</span>
        <input
          id="link"
          className="field-input"
          type="url"
          inputMode="url"
          value={link}
          placeholder="https://… (menu, reservation, your post)"
          onChange={(e) => onLink(e.target.value)}
        />
      </label>
    </section>
  );
}
